/* ================================================================ */
// ICON COMPONENT | Icon.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// Material UI
import Icon from '@material-ui/core/Icon';
import makeStyles from '@material-ui/styles/makeStyles';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        zIndex: 10,
        backgroundColor: theme.palette.secondary.contrastText,
        color: theme.palette.secondary.main,
        width: '3.5rem',
        height: '3.5rem'
    },
    icon: {

    }
}));
/* ========================================== */
/**
 * Icon Component
 * --------------
 * Outputs icon by type
 *
 * @param {string} props.label label for alt and aria tags
 * @param {string | element} props.icon default icon
 * @param {string} props.colour material ui colour
 * @param {object} props.styles style overrides
 */
const IconComponent = ({label, icon, colour, classes, styles}) => {
    //const classes = useStyles();
    if(typeof icon === 'string')
        // Resolve Material UI Icon
        return <Icon aria-label={label} classes={{root: classes}} styles={styles} color={colour}>{icon}</Icon>;
    else
        // Resolve SVG Icon
        return (
            <img
                className={classes.icon}
                src={`data:image/svg+xml;base64, ${btoa(icon)}`}
                alt={`${label} button icon`}
                style={styles}
            />
        );
}
/* ========================================== */
/**
 * Icon
 * ----
 * Dynamic icon outputting an image or predefined
 * material ui icon according to specified icon parameters
 *
 * @param {object} props component props
 * @param {string | element} props.icon default icon
 * @param {string | element} props.activeIcon icon for active state
 * @param {string} props.colour material ui colour
 * @param {string} props.activeColour material ui 'active' colour
 * @param {bool} props.isActive active state
 * @param {object} props.styles style overrides
 * @param {string} props.label label for alt and aria tags
 */
const IconHandler = React.memo(({
    label,
    icon,
    activeIcon,
    colour,
    activeColour,
    isActive,
    styles
}) => {
    const classes = useStyles();
    const iconClasses = classNames(classes.icon, {
        'isActive': isActive
    });
    return (
        <IconComponent
            classes={iconClasses}
            label={label}
            icon={(isActive && activeIcon) ? activeIcon : icon}
            colour={(isActive && activeColour) ? activeColour : colour}
            styles={styles}
        />
    );
}, areEqual);
IconHandler.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    activeIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    colour: PropTypes.string,
    activeColour: PropTypes.string,
    isActive: PropTypes.bool,
    styles: PropTypes.object
};
IconHandler.defaultProps = {
    icon: 'close',
    styles: {},
    colour: 'primary',
    activeColour: 'secondary',
    isActive: false
};
IconHandler.displayName = 'IconHandler';
function areEqual(prevProps, nextProps) {
    const isEqual = prevProps.isActive === nextProps.isActive ||
        prevProps.colour === nextProps.colour;
    console.debug(`${IconHandler.displayName} | `, 'Prop Check -', 'prevProps:', prevProps, 'nextProps:', nextProps, 'isEqual:', isEqual);
    return isEqual;
}
/* ========================================== */
// Export
/* ========================================== */
export default IconHandler;